import React, { useEffect, useState } from 'react';
import {
  Box, CircularProgress, Divider, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSelector } from 'react-redux';
import apiClient from '../../../../requests/api/apiClient';
import { GetSectorResumeDataResponse } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';

dayjs.extend(utc);

type ResumeData = GetSectorResumeDataResponse['sectorResumeData'];

interface DataRowProps {
  label: string;
  value: string;
}

const DataRow = ({ label, value }: DataRowProps) => (
  <Box display="flex" justifyContent="space-between" py={0.5}>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight="bold">
      {value}
    </Typography>
  </Box>
);

interface IrrigationDataTabProps {
  days: string[];
}

const IrrigationDataTab = ({ days }: IrrigationDataTabProps) => {
  const { selectedSector } = useSelector(
    (state: ReduxState) => state.currentSector,
  );
  const { selectedFarm } = useSelector(
    (state: ReduxState) => state.accountFarm,
  );

  const [resumeData, setResumeData] = useState<ResumeData | null>(null);
  const [loading, setLoading] = useState(false);

  const getSectorToUse = () => {
    if (!selectedSector || !selectedFarm) return null;
    if (selectedSector.parentSector
      && selectedSector.parentSector !== '1') {
      const parent = selectedFarm.sectors?.find(
        (s) => s.id === selectedSector.parentSector,
      );
      if (parent) return parent;
    }
    return selectedSector;
  };

  useEffect(() => {
    const sectorToUse = getSectorToUse();
    if (!sectorToUse?.id || !days.length) {
      setResumeData(null);
      return () => {};
    }

    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const startDate = dayjs(days[0]).utc().startOf('day')
          .format('YYYY-MM-DDTHH:mm:ss');
        const endDate = dayjs(days[days.length - 1]).utc().endOf('day')
          .format('YYYY-MM-DDTHH:mm:ss');
        const response = await apiClient.sector.getSectorResumeData(
          sectorToUse.id as string,
          startDate,
          endDate,
        );
        if (!cancelled) {
          setResumeData(response.data?.sectorResumeData);
        }
      } catch (err) {
        if (!cancelled) setResumeData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [selectedSector, selectedFarm, days]);

  if (!selectedSector) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="textSecondary">
          Selecciona un sector para ver los datos.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!resumeData) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="textSecondary">
          No hay datos disponibles.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={1}>
      <DataRow
        label="Kc riego del periodo"
        value={resumeData.kcIrrigation?.toFixed(2) ?? 'N/A'}
      />
      <Divider />
      <DataRow
        label="Riego real / día"
        value={`${resumeData.irrigationPerDay?.toFixed(2) ?? 'N/A'} mm`}
      />
      <Divider />
      <DataRow
        label="Riego acumulado"
        value={`${resumeData.irrigationAccumulatedResult?.toFixed(2) ?? 'N/A'} mm`}
      />
      <Divider />
      <DataRow
        label="ETo acumulado"
        value={`${resumeData.et0AccumulatedResult?.toFixed(2) ?? 'N/A'} mm`}
      />
    </Box>
  );
};

export default IrrigationDataTab;
